import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminHeader from '../../components/admin/AdminHeader';
import { buildApiUrl } from '../../config/api';
import '../../styles/admin-ai-chats.css';

const EMPTY_STATS = {
    total: 0,
    withPhone: 0,
    activeToday: 0
};

const AdminAIChats = () => {
    const [sessions, setSessions] = useState([]);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        phoneOnly: false
    });

    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('token');
        return {
            Authorization: `Bearer ${token}`,
            'x-auth-token': token
        };
    }, []);

    const fetchSessions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(buildApiUrl('/assistant/admin/sessions'), {
                headers: getHeaders()
            });

            const nextSessions = response.data?.data || [];
            setSessions(nextSessions);

            if (!selectedSessionId && nextSessions.length > 0) {
                setSelectedSessionId(nextSessions[0].id);
            }
        } catch (error) {
            console.error('Load AI chat sessions error:', error);
            alert(error.response?.data?.message || 'Failed to load AI chat sessions');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, selectedSessionId]);

    const fetchSessionDetails = useCallback(async (sessionId) => {
        if (!sessionId) return;

        try {
            setDetailLoading(true);
            const response = await axios.get(buildApiUrl(`/assistant/admin/sessions/${sessionId}`), {
                headers: getHeaders()
            });

            setSelectedSession(response.data?.data?.session || null);
            setMessages(response.data?.data?.messages || []);
        } catch (error) {
            console.error('Load AI chat detail error:', error);
            alert(error.response?.data?.message || 'Failed to load AI chat details');
        } finally {
            setDetailLoading(false);
        }
    }, [getHeaders]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    useEffect(() => {
        if (selectedSessionId) {
            fetchSessionDetails(selectedSessionId);
        }
    }, [fetchSessionDetails, selectedSessionId]);

    const filteredSessions = useMemo(() => {
        const query = filters.search.trim().toLowerCase();

        return sessions.filter((session) => {
            const haystack = [
                session.phone_number,
                session.guest_id,
                session.source_page,
                session.first_user_message,
                session.latest_assistant_message
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            if (filters.phoneOnly && !session.phone_number) {
                return false;
            }

            return !query || haystack.includes(query);
        });
    }, [filters, sessions]);

    const stats = useMemo(() => {
        if (!sessions.length) {
            return EMPTY_STATS;
        }

        const today = new Date().toDateString();

        return {
            total: sessions.length,
            withPhone: sessions.filter((session) => session.phone_number).length,
            activeToday: sessions.filter((session) => {
                const stamp = session.last_message_at || session.updated_at;
                return stamp && new Date(stamp).toDateString() === today;
            }).length
        };
    }, [sessions]);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const formatDateTime = (value) => {
        if (!value) return 'N/A';
        return new Date(value).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const selectedMetaPhone = selectedSession
        ? sessions.find((session) => session.id === selectedSession.id)?.phone_number || 'Not captured'
        : 'Not captured';

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-main ai-chats-admin-page">
                <AdminHeader title="AI Chat Sessions" />

                <section className="ai-chats-hero">
                    <div>
                        <span className="ai-chats-eyebrow">Assistant Inbox</span>
                        <h2>Track every AI conversation from one admin view</h2>
                        <p>
                            Review live chat sessions, see verified mobile numbers, and open complete customer
                            conversations without affecting the public chat flow.
                        </p>
                    </div>
                    <button type="button" className="ai-chats-refresh-btn" onClick={fetchSessions}>
                        <i className="fas fa-rotate-right"></i>
                        Refresh
                    </button>
                </section>

                <div className="stats-grid">
                    <div className="stat-card ai-chat-stat-card">
                        <div className="stat-icon"><i className="fas fa-comments"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Total Sessions</span>
                            <span className="stat-value">{stats.total}</span>
                        </div>
                    </div>
                    <div className="stat-card ai-chat-stat-card">
                        <div className="stat-icon"><i className="fas fa-phone"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">With Mobile</span>
                            <span className="stat-value">{stats.withPhone}</span>
                        </div>
                    </div>
                    <div className="stat-card ai-chat-stat-card">
                        <div className="stat-icon"><i className="fas fa-bolt"></i></div>
                        <div className="stat-details">
                            <span className="stat-label">Active Today</span>
                            <span className="stat-value">{stats.activeToday}</span>
                        </div>
                    </div>
                </div>

                <div className="filters-card ai-chat-filters-card">
                    <div className="filters-grid">
                        <div className="filter-group">
                            <label>Search Session</label>
                            <input
                                type="text"
                                name="search"
                                value={filters.search}
                                onChange={handleFilterChange}
                                placeholder="Search by phone, message, page or guest id"
                            />
                        </div>
                        <label className="ai-chat-phone-toggle">
                            <input
                                type="checkbox"
                                name="phoneOnly"
                                checked={filters.phoneOnly}
                                onChange={handleFilterChange}
                            />
                            <span>Show only verified mobile sessions</span>
                        </label>
                    </div>
                </div>

                <div className="ai-chats-layout">
                    <section className="ai-chat-session-list">
                        <div className="ai-chat-section-head">
                            <h3>All Sessions</h3>
                            <span>{filteredSessions.length} visible</span>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading chat sessions...</p>
                            </div>
                        ) : filteredSessions.length > 0 ? (
                            <div className="ai-chat-session-cards">
                                {filteredSessions.map((session) => (
                                    <button
                                        key={session.id}
                                        type="button"
                                        className={`ai-chat-session-card ${selectedSessionId === session.id ? 'active' : ''}`}
                                        onClick={() => setSelectedSessionId(session.id)}
                                    >
                                        <div className="ai-chat-session-card-top">
                                            <strong>Session #{session.id}</strong>
                                            <span>{session.message_count || 0} msgs</span>
                                        </div>
                                        <div className="ai-chat-session-phone">
                                            <i className="fas fa-phone"></i>
                                            <span>{session.phone_number || 'No mobile captured'}</span>
                                        </div>
                                        <p>{session.first_user_message || 'No customer message yet.'}</p>
                                        <div className="ai-chat-session-meta">
                                            <span>{session.source_page || 'Unknown page'}</span>
                                            <span>{formatDateTime(session.last_message_at || session.updated_at)}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <i className="fas fa-comments"></i>
                                <h3>No AI chat sessions found</h3>
                                <p>Sessions will appear here as soon as customers use the assistant.</p>
                            </div>
                        )}
                    </section>

                    <section className="ai-chat-detail-panel">
                        <div className="ai-chat-section-head">
                            <h3>Conversation Detail</h3>
                            {selectedSession ? <span>Session #{selectedSession.id}</span> : null}
                        </div>

                        {detailLoading ? (
                            <div className="loading-state compact">
                                <div className="spinner"></div>
                                <p>Loading conversation...</p>
                            </div>
                        ) : selectedSession ? (
                            <>
                                <div className="ai-chat-session-summary">
                                    <div className="summary-item">
                                        <label>Verified Mobile</label>
                                        <strong>{selectedMetaPhone}</strong>
                                    </div>
                                    <div className="summary-item">
                                        <label>Guest ID</label>
                                        <strong>{selectedSession.guest_id || 'Guest user'}</strong>
                                    </div>
                                    <div className="summary-item">
                                        <label>Source Page</label>
                                        <strong>{selectedSession.source_page || 'N/A'}</strong>
                                    </div>
                                    <div className="summary-item">
                                        <label>Updated</label>
                                        <strong>{formatDateTime(selectedSession.updated_at)}</strong>
                                    </div>
                                </div>

                                <div className="ai-chat-transcript">
                                    {messages.length > 0 ? messages.map((message) => (
                                        <div key={message.id} className={`ai-chat-admin-message ${message.role}`}>
                                            <div className="ai-chat-admin-badge">
                                                {message.role === 'assistant' ? 'AI' : 'User'}
                                            </div>
                                            <div className="ai-chat-admin-bubble">
                                                <p>{message.content}</p>
                                                <span>{formatDateTime(message.created_at)}</span>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="empty-state compact">
                                            <h3>No messages in this session</h3>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="empty-state">
                                <i className="fas fa-message"></i>
                                <h3>Select a session</h3>
                                <p>Open any customer chat from the left to read the full transcript.</p>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AdminAIChats;
